import React, { useEffect, useState } from "react";
import axios from "axios";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";

/**
 * Warning shown when the backend reports IS_DEV_SERVER=true, so a tester
 * or customer knows at a glance they are not looking at production data.
 *
 * Two variants: the default "strip" spans the top of the page; "chip" is
 * the compact pill in the top bar — the first clause of the text as its
 * label, the full text in its tooltip (and accessible name).
 *
 * Fails silent on any error so a missing endpoint or a transient
 * network blip doesn't put a scary banner on prod by accident.
 */
// Styles per server-reported banner_color. Anything unknown falls back
// to red so a misconfigured value still yields the loudest variant.
const BANNER_STYLES = {
  red: { bgcolor: "#b91c1c", color: "white" },
  yellow: { bgcolor: "#f59e0b", color: "#111827" },
};

const DEFAULT_TEXT =
  "Dev server — not production. Data here may be wiped without notice.";

/** The first clause of the banner text: up to a dash, a colon or the end
 *  of the first sentence ("TEST SERVER — NOT PRODUCTION. …" → "TEST SERVER"). */
export const chipLabel = (text) => {
  const clause = text.split(/\s[—–-]\s|:\s|\.(?:\s|$)/)[0].trim();
  return clause || text;
};

export default function DevServerBanner({ variant = "strip" }) {
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios
      .get("api/server/info/")
      .then((res) => {
        if (!cancelled && res?.data?.is_dev_server === true) {
          setBanner({
            style: BANNER_STYLES[res.data.banner_color] || BANNER_STYLES.red,
            text: res.data.banner_text || DEFAULT_TEXT,
          });
        }
      })
      .catch(() => {
        // Intentional: never show the banner on prod due to a network blip.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (!banner) return null;

  if (variant === "chip") {
    return (
      <Tooltip title={banner.text} arrow>
        <Box
          role="alert"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.6,
            px: 1.25,
            py: 0.45,
            borderRadius: 999,
            bgcolor: banner.style.bgcolor,
            color: banner.style.color,
            boxShadow: `0 0 0 3px ${banner.style.bgcolor}40`,
            fontSize: "0.7rem",
            fontWeight: 800,
            letterSpacing: 0.8,
            lineHeight: 1.2,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            cursor: "default",
          }}
        >
          <WarningAmberRoundedIcon sx={{ fontSize: 15 }} />
          {chipLabel(banner.text)}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Box
      role="alert"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: (theme) => theme.zIndex.appBar + 1,
        // No explicit width: a block-level Box already spans its parent, and
        // width:100% + horizontal padding overflows the page in x when
        // box-sizing isn't border-box.
        boxSizing: "border-box",
        maxWidth: "100%",
        bgcolor: banner.style.bgcolor,
        color: banner.style.color,
        py: 0.75,
        px: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,
        fontWeight: 700,
        letterSpacing: 0.5,
        textTransform: "uppercase",
        fontSize: "0.85rem",
        boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
      }}
    >
      <WarningAmberRoundedIcon sx={{ fontSize: 18 }} />
      {banner.text}
    </Box>
  );
}
